import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useComboList } from '../../../../shared/audit/multi-combo'
import { SEARCH_FIELD_PROPS } from '../../../../shared/component/search-field-props'
import { formatAgo, formatDateTime, serverMessage } from '../../../../shared/sso-workspace/format'
import { ErrorReference } from '../../../../shared/error/error-reference'
import { useErrorLine } from '../../../../shared/error/use-error-line'
import { getConnection, listChannels } from './alert-routing-api'
import { NewChannelDialog } from './new-channel-dialog'
import '../../../../shared/audit/audit.scss'
import './alert-routing.scss'

// The hint every channel field carries, in place of the ConfigSchema's own
// help: the app's name in that workspace is the connection's botName (each
// environment's Slack app has its own: "EducationXR", "[DEV] EducationXR";
// owner 2026-10-30), never written here; without one, "the EducationXR app".
export const privateChannelHint = botName => (botName
  ? `To use a private channel, type /invite @${botName} in it, then press Refresh.`
  : 'To use a private channel, add the EducationXR app to it in Slack, then press Refresh.')
export const PUBLIC_GROUP = 'Public channels'
export const privateGroup = botName => `Private channels ${botName || 'the EducationXR app'} is in`

// Typed text as a channel name Slack takes (new-channel-dialog.js
// CHANNEL_NAME): lowercase, accents dropped, quotes gone ("I'm" is "im"),
// spaces and runs of anything else one "-", from its first letter or digit,
// at most 80. Nothing usable is ''. "I'm a dog" is "im-a-dog".
export const slugOf = text => (text || '')
  .normalize('NFKD').replace(/[\u0300-\u036f]/g, '')
  .toLowerCase()
  .replace(/^#/, '')
  .replace(/['\u2018\u2019"\u201c\u201d`]/g, '')
  .replace(/[^a-z0-9_-]+/g, '-')
  .replace(/-{2,}/g, '-')
  .replace(/^[^a-z0-9]+/, '')
  .slice(0, 80)
  .replace(/-+$/, '')

const nameOf = c => `#${c.name}`

// A ConfigSchema `channel` field: a channel of the connected workspace its
// connection field names (connectionID). A searchable combo (the audit
// screens' look, placed by useComboList): public channels first, then the
// private channels the app was invited into; Refresh asks Slack again
// (refresh=1). ArrowUp/Down move, Enter picks, Escape or leaving the box puts
// the choice back.
//
// "+ New channel" is the list's footer, docked under it while the channels
// scroll (owner 2026-10-30: a long list hid it), the last stop for the arrow
// keys. Typed text that names no channel becomes "+ Create #<slug>" (slugOf),
// also when a few channels match it, unless one is called exactly that; with
// nothing matching the list says "No channel called #<slug>.". Either opens
// the new channel dialog (new-channel-dialog.js) with the name filled in,
// Public chosen, so it only needs confirming; nothing is made before that.
// Offered only when editable; in the route editor (inline) only when the
// workspace already lets the app create channels (canCreateChannels): asking
// for that permission would leave the page and lose the unsaved route. In the
// target drawer it is always offered, and the dialog asks for the permission
// when it is needed. The private channel hint under the field names the
// workspace's app (the connection's botName).
// fieldKey: this field's config key; pending: { provider, returnTo, draft }
// for that consent's way back.
export const ChannelPicker = ({ id, label, connectionID, value, knownName, onChange, invalid, describedBy, editable = true, inline = false, fieldKey, pending }) => {
  const [answer, setAnswer] = useState(null)
  const [loading, setLoading] = useState(false)
  const [error, failure, setError] = useErrorLine()
  const [text, setText] = useState(null) // null: closed
  const [active, setActive] = useState(0)
  const [connection, setConnection] = useState(null)
  const [creating, setCreating] = useState(null) // the name the dialog starts with, while it is open
  const seq = useRef(0)
  const open = text !== null
  const place = useComboList(open)

  // One list for the current workspace; a newer ask always wins.
  const load = useCallback(refresh => {
    const mine = ++seq.current
    if (!connectionID) {
      setAnswer(null)
      setLoading(false)
      setError(null)
      return
    }
    setLoading(true)
    listChannels(connectionID, refresh).then(v => {
      if (seq.current !== mine) return
      setError(null)
      setAnswer(v || { channels: [] })
      setLoading(false)
    }, err => {
      if (seq.current !== mine) return
      setAnswer(null)
      setError(serverMessage(err, "The channels couldn't be loaded."), err)
      setLoading(false)
    })
  }, [connectionID, setError])
  // Another workspace: its own list, never the last one's while it loads.
  useEffect(() => {
    setAnswer(null)
    load(false)
  }, [load])

  // The workspace's connection: may the app create channels, and its name there.
  useEffect(() => {
    setConnection(null)
    if (!connectionID) return undefined
    let live = true
    getConnection(connectionID).then(c => live && setConnection(c || null), () => live && setConnection(null))
    return () => { live = false }
  }, [connectionID])
  const mayCreate = Boolean(connection && connection.canCreateChannels)
  const botName = connection && connection.botName

  const typed = (text || '').trim().replace(/^#/, '')
  const needle = typed.toLowerCase()
  const all = (answer && answer.channels) || []
  const match = c => !needle || c.name.toLowerCase().includes(needle)
  const shownPublic = all.filter(c => !c.private && match(c))
  const shownPrivate = all.filter(c => c.private && match(c))
  const offerNew = editable && Boolean(connectionID) && (!inline || mayCreate)
  const slug = slugOf(typed)
  const exact = Boolean(slug) && all.some(c => c.name === slug)
  const footer = offerNew && !exact ? { id: '+new', isNew: true, slug } : null
  const list = [...shownPublic, ...shownPrivate, ...(footer ? [footer] : [])]
  const current = open ? list[Math.min(active, list.length - 1)] : undefined

  const found = all.find(c => c.id === value)
  const chosen = !value ? '' : found ? nameOf(found) : knownName ? `#${knownName}` : value

  const close = () => {
    setText(null)
    setActive(0)
  }
  const pick = row => {
    close()
    if (!row) return
    if (row.isNew) setCreating(row.slug)
    else if (row.id !== value) onChange(row.id)
  }
  const onKeyDown = e => {
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      if (!open) {
        setText('')
        setActive(0)
      } else setActive(a => Math.min(list.length - 1, a + 1))
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      setActive(a => Math.max(0, a - 1))
    } else if (e.key === 'Enter') {
      e.preventDefault()
      if (open && current) pick(current)
    } else if (e.key === 'Escape' && open) {
      e.stopPropagation()
      close()
    }
  }

  const listID = `${id}-list`
  const optID = row => `${id}-opt-${list.indexOf(row)}`
  const option = (row, text, className = '') =>
    <div key={row.id} id={optID(row)} role="option" aria-selected={!row.isNew && row.id === value}
      className={`au-opt${row === current ? ' au-active' : ''}${className}`}
      onMouseDown={e => { e.preventDefault(); pick(row) }}>
      <span className="au-opt-text">{text}</span>
    </div>
  const group = (title, rows) => rows.length > 0 &&
    <div role="group" aria-label={title}>
      <div className="au-combo-grp" aria-hidden="true">{title}</div>
      {rows.map(c => option(c, nameOf(c)))}
    </div>
  const hint = loading && !answer
    ? 'Loading channels…'
    : error
      ? "The channels couldn't be loaded."
      : answer && !shownPublic.length && !shownPrivate.length
        ? (slug ? `No channel called #${slug}.` : needle ? 'No channels match.' : 'No channels to pick from yet.')
        : null

  return (
    <div className="ar-channel">
      <div className="ar-row">
        <div className="au-combo ar-combo" ref={place.anchor}>
          <input
            {...SEARCH_FIELD_PROPS}
            id={id}
            name={id}
            className={`form-control${invalid ? ' is-invalid' : ''}`}
            role="combobox"
            aria-label={label}
            aria-expanded={open}
            aria-controls={listID}
            aria-autocomplete="list"
            aria-activedescendant={current ? optID(current) : undefined}
            aria-describedby={describedBy}
            disabled={!connectionID}
            placeholder={!connectionID ? 'Pick a workspace first' : open || !chosen ? 'Search channels' : undefined}
            value={open ? text : chosen}
            onChange={e => {
              setText(e.target.value)
              setActive(0)
            }}
            onFocus={() => {
              if (text === null) {
                setText('')
                setActive(0)
              }
            }}
            onBlur={close}
            onKeyDown={onKeyDown}
          />
          {open &&
            <div className="au-combo-list ar-channel-list" ref={place.list} style={place.style} role="listbox" id={listID} aria-label={label} aria-busy={loading}>
              {group(PUBLIC_GROUP, shownPublic)}
              {group(privateGroup(botName), shownPrivate)}
              {hint && <div className="au-combo-hint">{hint}</div>}
              {footer && option(footer, footer.slug ? `+ Create #${footer.slug}` : '+ New channel', ' ar-new-channel')}
            </div>}
        </div>
        <button type="button" className="btn sw-btn btn-sm" disabled={!connectionID || loading} onClick={() => load(true)}>Refresh</button>
        {answer && answer.fetchedAt && <span className="sw-hint" title={formatDateTime(answer.fetchedAt)}>{`Listed ${formatAgo(answer.fetchedAt)}`}</span>}
      </div>
      {error && <><p className="text-danger small m-0" role="alert">{error}</p><ErrorReference error={failure} compact /></>}
      <div className="sw-hint">{privateChannelHint(botName)}</div>
      {answer && answer.truncated && <p className="sw-hint m-0">Only part of this workspace's channels could be listed.</p>}
      {creating !== null &&
        <NewChannelDialog open connectionID={connectionID} channelKey={fieldKey} initialName={creating} inline={inline} pending={pending}
          onCancel={() => setCreating(null)}
          onCreated={channel => {
            setCreating(null)
            // listed at once: Slack's list may be a few minutes old
            setAnswer(a => (a && a.channels.some(c => c.id === channel.id) ? a : { ...(a || {}), channels: [...((a && a.channels) || []), channel] }))
            onChange(channel.id)
          }} />}
    </div>
  )
}
